import React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { Loader2 } from "lucide-react";

import { toast } from "sonner";
import { userProfileSchema } from "@/schemas/userProfileSchema";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSellerInfo, updateStylistInfo, updateUserInfo } from "@/graphql/mutations";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { setStylistInfo, setUser } from "@/store/slices/authSlice";
import ls from "@/utils/SecureLS";

type ProfileFormValues = z.infer<typeof userProfileSchema>;

const UserProfile = () => {

  const userLs = ls.get("user") || "";
  const user = JSON.parse(userLs || "{}");

  const stylistInfoLs = ls.get("stylistInfo") || "";
  const stylistInfo = JSON.parse(stylistInfoLs || "{}");


  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const defaultValues: ProfileFormValues = {
    firstName: user?.first_name || "",
    lastName: user?.last_name || "",
    email: user?.email || "",
    phone: stylistInfo?.phone || "",
    salonName: stylistInfo?.saloon_name || "",
  };

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(userProfileSchema),
    defaultValues,
  });
  const onSubmit = async (data: ProfileFormValues) => {
    setIsLoading(true);
    try {
      if (data?.firstName !== user?.first_name || data?.lastName !== user?.last_name) {
        const userInfo = await executeGraphQLMutation({
          query: updateUserInfo,
          variables: {
            input: {
              first_name: data?.firstName,
              last_name: data?.lastName,
              updated_at: new Date().toISOString(),
            },
          },
        });

        if (userInfo) {
          dispatch(setUser(userInfo));
        } else {
          toast.error("Failed to update user info");
          return;
        }
      }

      if (data?.phone !== stylistInfo?.phone || data?.salonName !== stylistInfo?.saloon_name) {
        const stylistInfoUpdated = await executeGraphQLMutation({
          query: updateStylistInfo,
          variables: {
            input: {
              id: stylistInfo?.id,
              phone: data?.phone,
              saloon_name: data?.salonName,
              updated_at: new Date().toISOString(),
            },
          },
        });

        if (stylistInfoUpdated) {
          dispatch(setStylistInfo(stylistInfoUpdated));
        } else {
          toast.error("Failed to update stylist info");
          return;
        }
       
      }

      toast.success("Profile updated", {
        description: "Your profile information has been successfully updated.",
      });
    } catch (error) {
      toast.error("Failed to update profile", {
        description: "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full h-full">
      <CardHeader>
        <CardTitle>Profile Information</CardTitle>
        <CardDescription>Update your account information here</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone number</FormLabel>
                  <FormControl>
                    <Input type="tel" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="salonName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Salon name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="bg-black text-white hover:bg-gray-800"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default UserProfile;
